<template>
  <div class="app-product-slider flex flex-col">
    <div v-if="categories.length > 1" class="app-product-slider__filters flex flex-wrap gap-2 px-4 py-6">
      <button
        class="app-product-slider__filter-button"
        :class="{ 'app-product-slider__filter-button--active': selectedCategory === null }"
        size="small"
        pill
        @click="selectCategory(null)"
      >
        {{ t('marketingPages.productSlider.allCategories', [items.length]) }}
      </button>

      <button
        v-for="category in categories"
        :key="category"
        class="app-product-slider__filter-button"
        :class="{ 'app-product-slider__filter-button--active': selectedCategory === category }"
        size="small"
        pill
        @click="selectCategory(category)"
      >
        {{ t('marketingPages.productSlider.categoryWithCount', [category, getCategoryCount(category)]) }}
      </button>
    </div>

    <div class="app-product-slider__content">
      <AppProductSliderHeader v-if="activeItem" :item="activeItem" class="app-product-slider__header lg:hidden" />

      <div
        class="app-product-slider__container relative w-full overflow-hidden"
        :class="{ 'app-product-slider__container--refreshed': isFilterJustChanged }"
      >
        <div
          ref="sliderRef"
          class="app-product-slider__slider flex w-full gap-4 px-4 lg:gap-1"
          @scroll.passive="updateScrollBounds"
        >
          <div
            v-for="(item, index) in filteredItems"
            :key="item._uid || index"
            :ref="slideRefs.set"
            class="app-product-slider__slide flex min-w-0 flex-none flex-col"
          >
            <AppProductSliderHeader :item class="hidden lg:flex" />

            <component
              :is="item.cta_url ? NuxtLink : 'div'"
              class="app-product-slider__image-container flex w-full items-center justify-center overflow-hidden rounded-t-[7%] shadow-xl lg:mx-4 lg:w-auto"
              v-bind="
                item.cta_url
                  ? {
                      to: item.cta_url,
                      'data-analytics-url': item.cta_url,
                      'data-analytics-link_text': item.title,
                    }
                  : {}
              "
            >
              <NuxtImg
                :src="item.image?.filename || ''"
                :alt="item.image?.alt || item.title || ''"
                :width="getImageDimensionsFromStoryblokSrc(item.image?.filename || '').width"
                :height="getImageDimensionsFromStoryblokSrc(item.image?.filename || '').height"
                class="app-product-slider__image size-full object-contain"
                provider="storyblok"
              />
            </component>
          </div>
        </div>

        <div class="app-product-slider__nav" aria-hidden="true">
          <button
            type="button"
            class="app-product-slider__nav-button app-product-slider__nav-button--prev"
            :aria-label="'Previous product'"
            :aria-disabled="isPrevDisabled ? 'true' : 'false'"
            :disabled="isPrevDisabled"
            @click="handlePrevClick"
          >
            <TappIcon icon="chevron-left" :size="24" />
          </button>

          <button
            type="button"
            class="app-product-slider__nav-button app-product-slider__nav-button--next"
            :aria-label="'Next product'"
            :aria-disabled="isNextDisabled ? 'true' : 'false'"
            :disabled="isNextDisabled"
            @click="handleNextClick"
          >
            <TappIcon icon="chevron-right" :size="24" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useDebounceFn, useIntersectionObserver, useTemplateRefsList } from '@vueuse/core';

  const { t } = useI18n();

  const NuxtLink = resolveComponent('NuxtLink');

  const { items } = defineProps<{
    items: MarketingProductSliderItemBlokStoryblok[];
  }>();

  const sliderRef = useTemplateRef('sliderRef');
  const slideRefs = useTemplateRefsList<HTMLDivElement>();
  const selectedCategory = ref<string | null>(null);
  const activeIndex = ref(0);
  const isFilterJustChanged = ref(false);
  const scrollLeft = ref(0);
  const maxScrollLeft = ref(0);

  /**
   * Both ends are derived from the scroll position rather than the observer-driven activeIndex,
   * which lags when several slides are visible and left the arrows enabled at the ends.
   */
  function updateScrollBounds() {
    const sliderElement = sliderRef.value;

    if (!sliderElement) {
      return;
    }

    scrollLeft.value = sliderElement.scrollLeft;
    maxScrollLeft.value = sliderElement.scrollWidth - sliderElement.clientWidth;
  }

  const isPrevDisabled = computed(() => scrollLeft.value <= 1);
  const isNextDisabled = computed(() => scrollLeft.value >= maxScrollLeft.value - 1);

  const categories = computed(() => {
    const cats = items.map((item) => item.category).filter(Boolean);
    return Array.from(new Set(cats));
  });

  const filteredItems = computed(() => {
    if (!selectedCategory.value) {
      return items;
    }
    return items.filter((item) => item.category === selectedCategory.value);
  });

  function scrollToIndex(index: number) {
    const targetSlide = slideRefs.value[index];
    const sliderElement = sliderRef.value;

    if (!targetSlide || !sliderElement) {
      return;
    }

    sliderElement.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: 'smooth',
    });
  }

  /**
   * The observer-driven activeIndex lags when several slides are visible at once, which left
   * the arrows targeting a slide that is already in view. Derive the step from the real scroll
   * position instead so each click always advances by exactly one slide.
   */
  function indexClosestToScroll() {
    const sliderElement = sliderRef.value;

    if (!sliderElement) {
      return 0;
    }

    let closestIndex = 0;
    let smallestDistance = Infinity;

    slideRefs.value.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - sliderElement.scrollLeft);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  function handlePrevClick() {
    if (isPrevDisabled.value) {
      return;
    }

    scrollToIndex(Math.max(indexClosestToScroll() - 1, 0));
  }

  function handleNextClick() {
    if (isNextDisabled.value) {
      return;
    }

    scrollToIndex(Math.min(indexClosestToScroll() + 1, filteredItems.value.length - 1));
  }

  const activeItem = computed(() => {
    if (filteredItems.value.length === 0) return undefined;
    return filteredItems.value[Math.min(activeIndex.value, filteredItems.value.length - 1)];
  });

  watch(filteredItems, () => {
    activeIndex.value = 0;
    nextTick(updateScrollBounds);
  });

  onMounted(() => {
    nextTick(updateScrollBounds);
  });

  function getCategoryCount(category: string) {
    return items.filter((item) => item.category === category).length;
  }

  const intersectionRatios = ref<Map<Element, number>>(new Map());

  function selectCategory(category: string | null) {
    selectedCategory.value = category;
    activeIndex.value = 0;

    const label =
      category === null
        ? t('marketingPages.productSlider.allCategories', [items.length])
        : t('marketingPages.productSlider.categoryWithCount', [category, getCategoryCount(category)]);

    pushToDataLayer({
      event: 'customEvent',
      'data-analytics-category': 'in-page',
      'data-analytics-action': 'click',
      'data-analytics-label': label,
    });

    // Visual feedback so filter change is obvious even when first items look the same
    isFilterJustChanged.value = true;
    const refreshDuration = 400;
    setTimeout(() => {
      isFilterJustChanged.value = false;
    }, refreshDuration);

    nextTick(() => {
      if (sliderRef.value) {
        sliderRef.value.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
      }
    });
  }

  const trackSwipe = useDebounceFn(() => {
    pushToDataLayer({
      event: 'navigation_interaction',
      action: 'swipe',
      label: filteredItems.value[activeIndex.value]?.title || '',
    });
  }, 300);

  useIntersectionObserver(
    slideRefs,
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          intersectionRatios.value.set(entry.target, entry.intersectionRatio);
        } else {
          intersectionRatios.value.delete(entry.target);
        }
      }

      let maxRatio = 0;
      let mostVisibleIndex = -1;

      for (const [element, ratio] of intersectionRatios.value) {
        if (ratio > maxRatio) {
          maxRatio = ratio;
          const index = slideRefs.value.findIndex((el) => el === element);
          if (index !== -1) {
            mostVisibleIndex = index;
          }
        }
      }

      if (mostVisibleIndex !== -1 && mostVisibleIndex !== activeIndex.value) {
        activeIndex.value = mostVisibleIndex;

        trackSwipe();
      }
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      root: sliderRef,
      rootMargin: '0px',
    },
  );

  watch(filteredItems, () => {
    intersectionRatios.value.clear();
  });
</script>

<style lang="postcss" scoped>
  .app-product-slider {
    &__content {
      background-color: var(--colors-primary-50);

      @media (--screen-lg) {
        background-color: transparent;
      }
    }

    &__filter-button {
      padding: var(--spacing-2) var(--spacing-4);
      border: 1px solid var(--selector-default-border-color);
      border-radius: var(--spacing-1);

      font: var(--typography-large-body-sm);

      background: var(--selector-default-background-color);

      &--active {
        border: 1px solid var(--selector-selected-border-color);
        color: var(--colors-white);
        background: var(--selector-selected-background-color);
      }
    }

    &__container {
      @media (--screen-lg) {
        width: auto;
        margin-right: calc(var(--spacing-9) * -1);
      }
    }

    &__slider {
      scrollbar-width: none;
      scroll-snap-type: x mandatory;

      overflow: auto hidden;

      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;

      @media (--screen-lg) {
        scroll-padding-left: var(--spacing-6);
        padding-right: 0;
        padding-left: var(--spacing-6);
      }

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__slide {
      scroll-snap-align: center;
      width: 78%;
    }

    /* The slide clips overflow, so the focus ring is drawn inside the link */
    a.app-product-slider__image-container:focus-visible {
      outline: 3px solid var(--colors-gray-900);
      outline-offset: -3px;
    }

    &__container--refreshed {
      animation: app-product-slider-refresh 0.4s ease;
    }

    &__nav {
      pointer-events: none;

      display: none;
      gap: var(--spacing-3);
      align-items: center;
      justify-content: end;

      padding: var(--spacing-4) 0;
      padding-inline: var(--spacing-3);
    }

    &__nav-button {
      pointer-events: auto;
      cursor: pointer;

      display: flex;
      align-items: center;
      justify-content: center;

      width: 50px;
      height: 50px;
      border: 0;
      border-radius: 9999px;

      background-color: var(--colors-white);
    }

    &__nav-button[aria-disabled='true'] {
      cursor: default;
      opacity: 0.4;
    }
  }

  @media (width >= 1024px) {
    .app-product-slider {
      &__nav {
        display: flex;
      }

      &__slide {
        scroll-snap-align: start;
        width: 320px;
        background-color: var(--colors-primary-50);
      }
    }
  }

  @keyframes app-product-slider-refresh {
    0%,
    100% {
      opacity: 1;
    }

    50% {
      opacity: 0.4;
    }
  }
</style>
